import { useCallback, useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { ArrowRight, Mail, Lock, AlertCircle } from 'lucide-react';
import { Eyebrow, RaceBib, EvidenceCard, MarginNote, Tom } from '@/components/foundry';

/**
 * KickstartV2SignIn — Foundry Race Bib chrome around the legacy Clerk
 * email-code flow. Builders enrolling via Kickstart already paid + got a
 * Clerk account at checkout; this is the resume entry point for cold visits
 * (e.g. clicking a continue-link from email).
 *
 * Doctrine: light-by-default, race-yellow accent, ink type, no concrete /
 * pit-garage holdovers from the legacy surface. Reuses the Clerk auth logic
 * pattern from `src/surfaces/kickstart/KickstartApp.tsx` so error messaging
 * and the email-code prepare/attempt flow stay consistent.
 */
export default function KickstartV2SignIn() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Friendly error mapping — mirrors the legacy KickstartSignIn so the auth
  // experience reads the same regardless of which surface the Builder uses.
  const mapAuthError = useCallback((value: unknown) => {
    const fallback = 'Unable to sign you in right now. Please try again.';
    if (!(value instanceof Error)) return fallback;
    const message = value.message?.trim() || fallback;
    const normalized = message.toLowerCase();
    if (
      normalized.includes("couldn't find") ||
      normalized.includes('not found') ||
      normalized.includes('no account') ||
      normalized.includes('identifier')
    ) {
      return 'We could not find a Kickstart account for that email. Use the same email you used at checkout.';
    }
    if (
      normalized.includes('code') ||
      normalized.includes('expired') ||
      normalized.includes('invalid')
    ) {
      return 'That sign-in code did not work. Check the latest email we sent and try again.';
    }
    return message;
  }, []);

  const handleSendCode = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!isLoaded || !signIn) return;
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        setError('Enter the email you used at checkout.');
        return;
      }

      setSubmitting(true);
      setError(null);
      setStatusMessage(null);

      try {
        const attempt = await signIn.create({
          strategy: 'email_code',
          identifier: normalizedEmail,
        });
        const factor = attempt.supportedFirstFactors?.find(
          (item): item is { strategy: 'email_code'; emailAddressId: string; safeIdentifier: string } =>
            item.strategy === 'email_code' && 'emailAddressId' in item,
        );

        if (!factor?.emailAddressId) {
          throw new Error('Email code sign-in is not available for this account.');
        }

        await attempt.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: factor.emailAddressId,
        });

        setEmail(normalizedEmail);
        setStep('code');
        setCode('');
        setStatusMessage(`Sent a sign-in code to ${factor.safeIdentifier || normalizedEmail}.`);
      } catch (authError) {
        setError(mapAuthError(authError));
      } finally {
        setSubmitting(false);
      }
    },
    [email, isLoaded, mapAuthError, signIn],
  );

  const handleVerifyCode = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!isLoaded || !signIn || !setActive) return;
      const trimmedCode = code.trim();
      if (!trimmedCode) {
        setError('Enter the sign-in code from your email.');
        return;
      }

      setSubmitting(true);
      setError(null);

      try {
        const attempt = await signIn.attemptFirstFactor({
          strategy: 'email_code',
          code: trimmedCode,
        });

        if (attempt.status !== 'complete' || !attempt.createdSessionId) {
          throw new Error('Sign-in did not complete.');
        }

        await setActive({ session: attempt.createdSessionId });
      } catch (authError) {
        setError(mapAuthError(authError));
      } finally {
        setSubmitting(false);
      }
    },
    [code, isLoaded, mapAuthError, setActive, signIn],
  );

  return (
    <div
      className="grid min-h-[calc(100dvh-64px)] place-items-center px-4 py-12 sm:px-6"
      style={{
        backgroundImage:
          'linear-gradient(var(--fd-grid) 1px, transparent 1px),' +
          'linear-gradient(90deg, var(--fd-grid) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        backgroundPosition: '-1px -1px',
      }}
    >
      <div className="w-full max-w-[420px]">
        {/* Brand framing */}
        <div className="mb-7 flex items-center gap-3">
          <Tom variant="walking-on" className="h-16 w-auto shrink-0" />
          <div>
            <RaceBib n="" label="Cohort 04 · Kickstart" tone="race" size="sm" shadow="sm" />
            <h1 className="mt-2 font-geist text-[24px] font-medium leading-[1.1] tracking-[-0.02em]">
              Welcome back.
            </h1>
            <p className="mt-1 text-[13.5px] leading-[1.5] text-fd-text-muted">
              Pick up where you left off. Same email you used at checkout — we'll send a
              one-time code.
            </p>
          </div>
        </div>

        {/* Card — race-bib stamped */}
        <div
          className="relative rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-6"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <Eyebrow className="mb-4 block">
            STEP {step === 'email' ? '01' : '02'} · {step === 'email' ? 'YOUR EMAIL' : 'VERIFY CODE'}
          </Eyebrow>

          {error && (
            <div className="mb-4 flex items-start gap-2.5 rounded-[8px] border-[1.5px] border-fd-coral/40 bg-fd-coral/5 px-3.5 py-2.5">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-fd-coral" />
              <p className="text-[13px] leading-[1.5] text-fd-text">{error}</p>
            </div>
          )}

          {statusMessage && !error && (
            <div className="mb-4">
              <EvidenceCard kind="premise" eyebrow="CODE SENT">
                {statusMessage}
              </EvidenceCard>
            </div>
          )}

          {step === 'email' ? (
            <form className="space-y-4" onSubmit={handleSendCode}>
              <div>
                <label
                  htmlFor="kickstart-v2-email"
                  className="mb-1.5 block font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle"
                >
                  Email address
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fd-text-subtle" />
                  <input
                    id="kickstart-v2-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@brand.com"
                    className="h-11 w-full rounded-[6px] border-[1.5px] border-fd-border bg-fd-bg pl-10 pr-3 font-geist text-[14px] text-fd-text outline-none transition-colors placeholder:text-fd-text-subtle/60 focus:border-fd-text"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={!isLoaded || submitting}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[13px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
              >
                {submitting ? 'Sending…' : <>Send sign-in code <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handleVerifyCode}>
              <div>
                <label
                  htmlFor="kickstart-v2-code"
                  className="mb-1.5 block font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle"
                >
                  Sign-in code
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fd-text-subtle" />
                  <input
                    id="kickstart-v2-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    className="h-11 w-full rounded-[6px] border-[1.5px] border-fd-border bg-fd-bg pl-10 pr-3 font-geist-mono text-[16px] tracking-[0.12em] text-fd-text outline-none transition-colors placeholder:text-fd-text-subtle/60 focus:border-fd-text"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={!isLoaded || submitting}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[13px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
              >
                {submitting ? 'Verifying…' : <>Verify and continue <ArrowRight className="h-4 w-4" /></>}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setStatusMessage(null);
                  setError(null);
                }}
                className="inline-flex h-9 w-full items-center justify-center gap-1.5 font-geist text-[12.5px] font-medium text-fd-text-muted transition-colors hover:text-fd-text"
              >
                Use a different email
              </button>
            </form>
          )}
        </div>

        {/* Footer help */}
        <MarginNote tilt={-3} tone="teal" size="sm" className="mt-5 inline-block">
          stuck? email <b>tom@theaibrandlab.com</b>
        </MarginNote>
      </div>
    </div>
  );
}
